<style lang="less" scoped>
.success {
    color: green;
}
.error {
    color: red;
}
.warning {
    color: darkorange;
}
.upload-status {
    .icon {
        font-size: 30px;
        line-height: 30px;
        vertical-align: middle;
        &:before {
            vertical-align: unset;
            color: inherit;
        }
    }
}
.dfNavButton.btn-file {
    border: 2px dashed #aaaaaa;
    border-radius: 5px;

    input[type="file"] {
        border: unset;
    }
}
</style>
<template>
    <div class="form-group focus-form-group file-form-group clearfix" :class="[settings.isChild ? 'form-group-child' : null, colClass, customErrorClass]">
        <p class="pdf-inline" v-if="isViewOnly">
            <strong class="display-label">{{ effectiveLabel }}:</strong>
            <span class="display-value" v-if="hasValue">
                <a target="_blank" :href="data.fileUrl">
                    <span class="icon icon-download"></span>
                    {{ data.fileName }}
                </a>
            </span>
            <button class="passthrough icon icon-edit mr-1" type="button" v-if="showAdminEditControls" @click.prevent="startAdminEdit"><span class="sr-only">Edit</span></button>
        </p>
        <template v-else>
            <label :aria-label="effectiveLabelText" :id="vmId + '_Label'" class="control-label" :for="vmId" :class="{ dfRequired: settings.required, dfOptional: !settings.required, 'sr-only': effectiveLabelText != effectiveLabel }">
                {{ effectiveLabelText }}
                <HelpIcon :label="effectiveLabelText" :text="settings.helpText" />
                <button class="passthrough icon icon-close mr-1" type="button" v-if="isAdminEditing" @click.prevent="cancelAdminEdit" aria-label="Stop Edit" />
            </label>
            <div v-show="hasValue">
                <div class="btn-group">
                    <span :id="vmId + '_HasValueDesc'" class="sr-only">You have selected the file: {{ filename }}.</span>
                    <a ref="uploadedFileRef" :tabindex="url == null ? undefined : 0" :aria-labelledby="vmId + '_Label'" :aria-describedby="vmId + '_HasValueDesc ' + errorIds" class="btn btn-outline-secondary rounded-left ml-0" target="_blank" :href="url" style="flex: auto; border-right: 0">{{ filename }}</a>
                    <button ref="removeFileRef" type="button" @click="removeFile" tabindex="0" role="button" class="btn btn-outline-danger input-group-append d-flex align-items-center" aria-label="Remove File" :aria-describedby="url == null ? vmId + '_Label ' + vmId + '_HasValueDesc ' + errorIds : undefined">&times;</button>
                </div>
            </div>
            <div :class="{ 'sr-only': hasValue }">
                <div class="btn dfNavButton btn-file d-flex flex-wrap justify-content-between align-items-center px-3 py-2">
                    <div class="d-flex py-2 flex-grow-1">
                        <input type="file" class="p-0" data-test-id="upload" :id="vmId" :aria-required="settings.required ? 'true' : 'false'" :aria-describedby="errorIds" ref="fileRef" @change="uploadFile" @abort="uploadFile" :tabindex="hasValue ? -1 : 0" />
                        <LoadingWheel :width="30" v-show="uploadStatus == 'in-progress'"></LoadingWheel>
                    </div>
                    <div class="upload-status" aria-live="polite">
                        <template v-if="uploadStatus != null">
                            <span v-if="uploadStatus == 'ok' && valid">
                                <span class="icon icon-check success" aria-hidden="true"></span>
                                <span class="sr-only">File uploaded successfully</span>
                            </span>
                            <span v-else-if="uploadStatus == 'too-large'" class="error">
                                <span class="icon icon-attention" role="img" aria-label="Error:"></span>
                                File must not be larger than {{ maxFileSizeForDisplay }}
                            </span>
                            <span v-else-if="uploadStatus == 'avScanFail'" class="error">
                                <span class="icon icon-attention" role="img" aria-label="Error:"></span>
                                Potential virus or malware detected.
                            </span>
                            <span v-else-if="uploadStatus == 'fail'" class="error">
                                <span class="icon icon-attention" role="img" aria-label="Error:"></span>
                                Unknown error
                            </span>
                        </template>
                    </div>
                </div>
            </div>
        </template>
        <ErrorList :data="data" :id="errorId" />
        <Comments :data="data" />
        <AdminSettingsDifferences :data="data" />
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, watch, computed } from "vue";
import { useMainStore } from "@/mainStore";
import { useObservable } from "@vueuse/rxjs";
import { props, Props, components, setup } from "./BaseDefMixin.3";
import FileDef from "../elements/FileDef";
import { FileSettings } from "@cs-ts/file-settings";

import LoadingWheel from "@/lib/LoadingWheel.3.vue";

export default defineComponent({
    components: {
        LoadingWheel,
        ...components,
    },
    props: props,
    //@ts-ignore
    setup(props: Props<FileDef, FileSettings>) {
        const base = setup<FileDef, FileSettings>(props);
        const fileRef = ref<HTMLInputElement>(null!);
        const uploadedFileRef = ref<HTMLElement>(null!);
        const removeFileRef = ref<HTMLElement>(null!);

        const mainStore = useMainStore();

        const hasValue2 = useObservable(props.data.hasValue$());
        const effectiveLabel = useObservable(props.data.effectiveLabel$());
        const effectiveLabelText = useObservable(props.data.effectiveLabelText$());
        const valid = useObservable(props.data.valid$());

        const hasValue = computed(() => {
            return hasValue2.value || props.data.valueId || props.data._hasValue;
        });
        const filename = computed(() => {
            return props.data.fileName;
        });
        const url = computed(() => {
            return props.data.fileUrl;
        });
        type UploadStatusType = "ok" | "fail" | "too-large" | "in-progress" | null;
        const uploadStatus = computed({
            get() {
                return props.data.uploadStatus as UploadStatusType;
            },
            set(value) {
                //eslint-disable-next-line vue/no-mutating-props
                props.data.uploadStatus = value;
            },
        });
        const maxFileSize = computed(() => {
            const globalSizeInMb = mainStore.globalConfigData.MaxUploadFileSizeInMb;
            const inputSize = base.settings.value.maxFileSize;
            if (globalSizeInMb != null && globalSizeInMb > 0) {
                const globalSize = globalSizeInMb * 1024 * 1024;
                if (inputSize != null && inputSize > 0) {
                    return Math.min(globalSize, inputSize);
                } else {
                    return globalSize;
                }
            } else {
                if (inputSize != null && inputSize > 0) {
                    return inputSize;
                } else {
                    return 1024 * 1024 * 1024;
                }
            }
        });
        const maxFileSizeForDisplay = computed(() => {
            const maxFileSizeV = maxFileSize.value;
            if (maxFileSizeV >= 1024 * 1024) {
                const inMb = Math.floor(maxFileSizeV / 1024 / 102.4) / 10;
                return `${inMb} MB`;
            } else if (maxFileSizeV >= 1024) {
                const inKb = Math.floor(maxFileSizeV / 1024);
                return `${inKb} KB`;
            } else {
                return `${maxFileSizeV} bytes`;
            }
        });

        const removeFile = () => {
            fileRef.value.value = null!;
            uploadStatus.value = null;
            base.pageStore.setFileInfoByVmId({
                id: base.vmId.value,
                valueId: undefined,
                contentType: undefined,
                fileName: undefined,
                fileUrl: undefined,
                hasValue: false,
            });
            fileRef.value.focus();
            mainStore.setLiveRegion(Promise.resolve("File has been removed successfully"));
        };
        // TODO - store action?
        const uploadFile = async () => {
            uploadStatus.value = "in-progress";
            const files = fileRef.value.files ?? [];
            if (files.length == 0) {
                removeFile();
                return;
            }
            const newFile = files[0];
            if (newFile.size > maxFileSize.value) {
                // Add delay on updating too-large status to properly announce the error on aria-live
                setTimeout(() => {
                    uploadStatus.value = "too-large";
                }, 100);
            } else {
                const filePromise = await base.pageStore.uploadExternalFile({ vmId: base.vmId.value, newFile });
                uploadStatus.value = (filePromise?.status as UploadStatusType) ?? "fail";
            }
            if (uploadStatus.value == "ok") {
                const elementToFocus = url.value == null ? removeFileRef.value : uploadedFileRef.value;
                elementToFocus.focus();
            } else {
                fileRef.value.focus();
            }
        };

        watch(uploadStatus, value => {
            if (value == null) {
                removeFile();
            }
        });

        base.focus = () => {
            if (hasValue.value) {
                uploadedFileRef.value.focus();
            } else {
                fileRef.value.focus();
            }
        };

        return {
            effectiveLabel,
            effectiveLabelText,
            hasValue,
            valid,
            filename,
            url,
            uploadStatus,
            maxFileSizeForDisplay,

            removeFile,
            uploadFile,

            fileRef,
            uploadedFileRef,
            removeFileRef,

            ...base.expose,
        };
    },
});
</script>
